import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { Effect, Match, Schema } from "effect";
import "@fontsource-variable/fraunces/opsz.css";
import {
  BellRing,
  Check,
  ChefHat,
  ChevronDown,
  CircleSlash,
  HandCoins,
  RefreshCw,
  Undo2,
  type LucideIcon,
} from "lucide-react";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { ApiError, Credential } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectQuery } from "#effect-query.ts";
import { OrderEvents, type ConnectionState } from "#order-events.ts";
import {
  formatMoney,
  formatTime,
  Order,
  orderSchema,
  singleOrderSchema,
} from "#orders.ts";
import { OrderLines } from "#order-dialog.tsx";
import "#order-tracking.css";

const eventSchema = Schema.Struct({
  type: Schema.Literal("order.updated"),
  payload: orderSchema,
});

/** Public single-order view using an expiring, order-scoped credential. */
export function OrderTracking() {
  const { orderId } = useParams();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const liveSequence = useRef(0);

  const [credential] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const query = new URLSearchParams(window.location.search);
    const token = hash.get("token") ?? query.get("token");

    return token ? new Credential(token) : undefined;
  });

  const [connection, setConnection] = useState<ConnectionState>("connecting");
  // Remove legacy query tokens from the address before navigation or telemetry.
  useEffect(() => {
    if (window.location.search)
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${credential ? `#token=${encodeURIComponent(credential.reveal())}` : ""}`,
      );
  }, [credential]);

  const orderQueryKey = useMemo(
    () => ["orders", "tracking", orderId] as const,
    [orderId],
  );
  const tracking = useQuery<Order, ApiError>({
    queryKey: orderQueryKey,
    enabled: Boolean(orderId),
    retry: false,
    queryFn: ({ signal }) => {
      const input = {
        path: `/orders/${encodeURIComponent(orderId ?? "")}`,
        schema: singleOrderSchema,
        signal,
      };
      const request = credential
        ? api.publicRequestEffect(input, credential)
        : api.requestEffect(input);

      return runEffectQuery(
        request.pipe(
          Effect.flatMap((response) => {
            const found = response.data.find(
              (item) => item.squareId === orderId,
            );

            return found
              ? Effect.succeed(found)
              : Effect.fail(new ApiError(404, "Order not found."));
          }),
        ),
        signal,
      );
    },
  });
  useEffect(() => {
    if (!orderId) return;

    const events = new OrderEvents({
      config: api.config,
      channel: `/default/${orderId}`,
      credential: async () =>
        credential
          ? { ok: true, value: credential }
          : api.snapshot().kind === "authenticated"
            ? api.credential()
            : {
                ok: false,
                error: new ApiError(401, "Sign in or scan a tracking QR."),
              },
      state: setConnection,
      resynchronise: () => {
        void queryClient.invalidateQueries({ queryKey: orderQueryKey });
      },
      event: (value) => {
        const parsed = decodeUnknownOption(eventSchema, value);

        // An event this page cannot read still means the order changed:
        // refetch instead of silently leaving a stale status on screen.
        if (!parsed) {
          void queryClient.invalidateQueries({ queryKey: orderQueryKey });
          return;
        }

        if (parsed.payload.squareId === orderId) {
          const sequence = ++liveSequence.current;

          void queryClient.cancelQueries({ queryKey: orderQueryKey }).then(() => {
            if (sequence === liveSequence.current)
              queryClient.setQueryData(orderQueryKey, parsed.payload);
          });
        }
      },
    });

    events.start();

    const visible = () => {
      if (document.visibilityState === "visible") {
        events.stop();
        events.start();
        void queryClient.invalidateQueries({ queryKey: orderQueryKey });
      }
    };

    document.addEventListener("visibilitychange", visible);

    return () => {
      events.stop();
      document.removeEventListener("visibilitychange", visible);
    };
  }, [api, orderId, credential, queryClient, orderQueryKey]);

  const loaded = tracking.data !== undefined;
  const ready = tracking.data?.state === "order_ready";

  // A customer may watch this tab from the queue: say when it's time to collect.
  useEffect(() => {
    if (!loaded) return;
    const previous = document.title;
    document.title = ready
      ? "Ready to collect · Pocket Rice"
      : "Your order · Pocket Rice";
    return () => {
      document.title = previous;
    };
  }, [loaded, ready]);

  const result = Match.value(tracking).pipe(
    Match.when({ status: "pending" }, () => (
      <section className="ct-card ct-waiting">
        <BrandMark className="ct-watermark" />
        <h1>Order tracking</h1>
        <p role="status">Loading your order…</p>
      </section>
    )),
    Match.when({ status: "error" }, ({ error }) =>
      Match.valueTags(error, {
        ApiError: (failure) => (
          <section className="ct-card ct-waiting">
            <BrandMark className="ct-watermark" />
            <h1>Order tracking</h1>
            <p role="alert" className="ct-alert">
              {failure.status === 401 || failure.status === 403
                ? "This tracking link has expired or is not valid for this order. Ask staff for a new QR code."
                : failure.message}
            </p>
          </section>
        ),
      }),
    ),
    Match.when({ status: "success" }, ({ data: order }) => {
      const stage = trackingStage(order);
      const { heading, detail, Icon } = stageCopy(stage, order);
      const count = itemCount(order);

      return (
        <>
          <section className={`ct-card ct-status ct-${stage}`}>
            <BrandMark className="ct-watermark" />
            <p className="ct-eyebrow">
              Your order · {formatTime(order.createdAt)}
            </p>
            <h1 className="ct-name">{order.label}</h1>
            <div className="ct-message">
              <span className="ct-icon" aria-hidden="true">
                <Icon size={26} strokeWidth={2.25} />
              </span>
              <div>
                <h2>{heading}</h2>
                <p>{detail}</p>
              </div>
            </div>
            <Progress stage={stage} />
            <p className={`ct-live ${connection === "live" ? "is-live" : ""}`}>
              <span className="ct-live-dot" aria-hidden="true" />
              <span>
                {connection === "live"
                  ? "Live updates connected"
                  : "Reconnecting…"}
              </span>
            </p>
          </section>
          <details className="ct-card tracking-details">
            <summary>
              <span className="ct-summary-text">See order details</span>
              <span className="ct-summary-meta">
                {count === 1 ? "1 item" : `${count} items`} ·{" "}
                {formatMoney(order.total)}
              </span>
              <ChevronDown
                className="ct-chevron"
                size={20}
                aria-hidden="true"
              />
            </summary>
            <OrderLines order={order} />
            <div className="order-total">
              <strong>Total</strong>
              <strong>{formatMoney(order.total)}</strong>
            </div>
          </details>
        </>
      );
    }),
    Match.exhaustive,
  );

  return (
    <main className="customer-tracking">
      <header className="ct-header">
        <Link to="/login" className="ct-lockup">
          <BrandMark className="ct-logo" />
          <span className="ct-wordmark">
            <strong>Pocket Rice</strong>
            <span aria-hidden="true">おにぎり · Onigiri</span>
          </span>
        </Link>
      </header>
      {result}
      <footer className="ct-footer">
        <button
          type="button"
          className="ct-refresh"
          disabled={tracking.isFetching}
          onClick={() => void tracking.refetch()}
        >
          <RefreshCw size={18} aria-hidden="true" />
          Refresh order
        </button>
        <a
          className="ct-instagram"
          href="https://www.instagram.com/pocket_rice_byronbay/"
          rel="noreferrer"
        >
          Follow Pocket Rice on Instagram
        </a>
      </footer>
    </main>
  );
}

type Stage =
  | "counter"
  | "preparing"
  | "ready"
  | "collected"
  | "cancelled"
  | "refunded";

function trackingStage(order: Order): Stage {
  if (order.awaitsCounterPayment) return "counter";
  return Match.value(order.state).pipe(
    Match.when("order_ready", () => "ready" as const),
    Match.when("order_completed", () => "collected" as const),
    Match.when("order_canceled", () => "cancelled" as const),
    Match.when("order_refunded", () => "refunded" as const),
    Match.orElse(() => "preparing" as const),
  );
}

function stageCopy(
  stage: Stage,
  order: Order,
): { heading: string; detail: string; Icon: LucideIcon } {
  return Match.value(stage).pipe(
    Match.when("counter", () => ({
      heading: "Pay at the stall to start your order",
      detail: `Show this page at the Pocket Rice stall and pay ${formatMoney(order.total)}. We’ll start making it as soon as you’ve paid.`,
      Icon: HandCoins,
    })),
    Match.when("preparing", () => ({
      heading: "We’re preparing your order",
      detail:
        "We’ll call your name when it’s ready. You can keep this page open for updates.",
      Icon: ChefHat,
    })),
    Match.when("ready", () => ({
      heading: "Your onigiri is ready!",
      detail: "Please come and collect your order from the stall.",
      Icon: BellRing,
    })),
    Match.when("collected", () => ({
      heading: "Enjoy your onigiri!",
      detail: "Thanks for ordering with Pocket Rice. See you at the markets.",
      Icon: Check,
    })),
    Match.when("cancelled", () => ({
      heading: "This order was cancelled",
      detail: "If that doesn’t sound right, please talk to us at the stall.",
      Icon: CircleSlash,
    })),
    Match.when("refunded", () => ({
      heading: "This order was refunded",
      detail: "If you have any questions, please talk to us at the stall.",
      Icon: Undo2,
    })),
    Match.exhaustive,
  );
}

/** Where the order is between ordering and collection, as on the kiosk receipt. */
function Progress({ stage }: { readonly stage: Stage }) {
  if (stage === "cancelled" || stage === "refunded") return null;

  const steps = [
    stage === "counter" ? "Pay at the stall" : "Order placed",
    "Preparing",
    "Ready",
    "Collected",
  ];
  const current = Match.value(stage).pipe(
    Match.when("counter", () => 0),
    Match.when("preparing", () => 1),
    Match.when("ready", () => 2),
    Match.orElse(() => steps.length),
  );

  return (
    <ol className="ct-progress" aria-label="Order progress">
      {steps.map((step, index) => {
        const state =
          index < current ? "done" : index === current ? "current" : "next";
        return (
          <li
            key={step}
            className={`is-${state}`}
            aria-current={state === "current" ? "step" : undefined}
          >
            <span className="ct-step-dot" aria-hidden="true">
              {state === "done" && <Check size={13} strokeWidth={3} />}
            </span>
            <span className="ct-step-label">{step}</span>
          </li>
        );
      })}
    </ol>
  );
}

function itemCount(order: Order) {
  return order.lines.reduce((count, line) => {
    const quantity = Number(line.quantity);
    return count + (Number.isInteger(quantity) && quantity > 0 ? quantity : 1);
  }, 0);
}

/** The onigiri お mark, tinted by `color`; the knockout PNG is used as a mask. */
function BrandMark({ className }: { readonly className: string }) {
  return <span aria-hidden="true" className={`ct-mark ${className}`} />;
}
